import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../contexts/FeaturesContext';
import NotificationBell from './NotificationBell';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import Logo from './Logo';

function LanguageSwitcher() {
  const { t } = useTranslation();
  const current = i18n.language;

  function switchTo(lang: string) {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  }

  return (
    <div className="flex items-center gap-1 text-xs font-medium">
      <button
        onClick={() => switchTo('de')}
        className={`px-1.5 py-0.5 rounded transition-colors ${
          current === 'de'
            ? 'bg-primary-600 text-white'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {t('nav.langDE')}
      </button>
      <span className="text-gray-300">|</span>
      <button
        onClick={() => switchTo('en')}
        className={`px-1.5 py-0.5 rounded transition-colors ${
          current === 'en'
            ? 'bg-primary-600 text-white'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {t('nav.langEN')}
      </button>
    </div>
  );
}

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { features } = useFeatures();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  function handleLogout() {
    logout();
    navigate('/');
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-gray-600 hover:text-gray-900'}`;

  return (
    <nav className="bg-white border-b border-primary-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <Logo size={104} variant="full" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {features.liga && <NavLink to="/leagues" className={linkClass}>{t('nav.leagues')}</NavLink>}
            {features.saison && <NavLink to="/seasons" className={linkClass}>{t('nav.seasons')}</NavLink>}
            {features.saison && <NavLink to="/ranking" className={linkClass}>{t('nav.ranking')}</NavLink>}
            <NavLink to="/courts" className={linkClass}>{t('nav.courts')}</NavLink>
            {user && <NavLink to="/dashboard" className={linkClass}>{t('nav.dashboard')}</NavLink>}
            {user && (features.liga || features.saison) && <NavLink to="/pairs" className={linkClass}>{t('nav.myPair')}</NavLink>}
            {user && (features.mannschaft || features.saison) && <NavLink to="/teams" className={linkClass}>{t('nav.myTeam')}</NavLink>}
            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `text-sm font-semibold px-3 py-1.5 rounded-md transition-colors ${
                    isActive
                      ? 'bg-accent-500 text-white'
                      : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                  }`
                }
              >
                {t('nav.admin')}
              </NavLink>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <NotificationBell />
                <div className="hidden md:flex items-center gap-3">
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    {user.photo_url ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL ?? ''}${user.photo_url}`}
                        alt={t('nav.profilePhoto')}
                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-800 flex items-center justify-center font-semibold text-sm border border-primary-200">
                        {user.first_name[0]}{user.last_name[0]}
                      </div>
                    )}
                    <span>{user.first_name}</span>
                  </Link>
                  <button onClick={handleLogout} className="btn btn-secondary btn-sm">
                    {t('nav.logout')}
                  </button>
                </div>
              </>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" className="btn btn-secondary btn-sm">{t('nav.login')}</Link>
                <Link to="/register" className="btn btn-primary btn-sm">{t('nav.register')}</Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('nav.menu')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3 flex flex-col gap-2">
          {features.liga && (
            <NavLink to="/leagues" className={linkClass} onClick={() => setMobileOpen(false)}>
              {t('nav.leagues')}
            </NavLink>
          )}
          {features.saison && (
            <NavLink to="/seasons" className={linkClass} onClick={() => setMobileOpen(false)}>
              {t('nav.seasons')}
            </NavLink>
          )}
          {features.saison && (
            <NavLink to="/ranking" className={linkClass} onClick={() => setMobileOpen(false)}>
              {t('nav.ranking')}
            </NavLink>
          )}
          <NavLink to="/courts" className={linkClass} onClick={() => setMobileOpen(false)}>
            {t('nav.courts')}
          </NavLink>
          {user && (
            <NavLink to="/dashboard" className={linkClass} onClick={() => setMobileOpen(false)}>
              {t('nav.dashboard')}
            </NavLink>
          )}
          {user && (features.liga || features.saison) && (
            <NavLink to="/pairs" className={linkClass} onClick={() => setMobileOpen(false)}>
              {t('nav.myPair')}
            </NavLink>
          )}
          {user && (features.mannschaft || features.saison) && (
            <NavLink to="/teams" className={linkClass} onClick={() => setMobileOpen(false)}>
              {t('nav.myTeam')}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/admin"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block text-sm font-semibold px-3 py-1.5 rounded-md transition-colors ${
                  isActive
                    ? 'bg-accent-500 text-white'
                    : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                }`
              }
            >
              {t('nav.admin')}
            </NavLink>
          )}
          {user ? (
            <>
              <NavLink to="/profile" className={linkClass} onClick={() => setMobileOpen(false)}>
                {t('profile.title')}
              </NavLink>
              <button onClick={handleLogout} className="block w-full text-left text-sm text-red-600 font-medium py-1">
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-sm font-medium text-gray-600" onClick={() => setMobileOpen(false)}>
                {t('nav.login')}
              </Link>
              <Link to="/register" className="block text-sm font-medium text-primary-600" onClick={() => setMobileOpen(false)}>
                {t('nav.register')}
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
